import React from 'react';
import { useQuery } from 'react-query';
import { Alert, Avatar, Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import keys from '../../common/react-query/data-keys';
import { formatDateTimeDescriptive } from '../../common/utils/common.util';
import { getInvoiceHistory } from './invoice.api';
import { InvoiceHistoryDTO } from './invoice.type';
import { formatInvoiceHistoryValue, INVOICE_HISTORY_FIELD_LABELS } from './invoice.util';

interface InvoiceHistoryDialogProps {
  open: boolean;
  invoiceId?: string;
  invoiceNumber?: string;
  onClose: () => void;
}

const ACTION_LABELS: Record<InvoiceHistoryDTO['action'], string> = {
  Created: 'created this invoice',
  Updated: 'edited this invoice',
  Deleted: 'deleted this invoice',
};

const ACTION_COLORS: Record<InvoiceHistoryDTO['action'], string> = {
  Created: 'success.main',
  Updated: 'primary.main',
  Deleted: 'error.main',
};

const getUserName = (entry: InvoiceHistoryDTO) =>
  [entry.changedBy?.firstName, entry.changedBy?.lastName].filter(Boolean).join(' ') || 'System';

const getInitials = (name: string) =>
  name
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const HistoryTimeline: React.FC<{ invoiceId: string }> = ({ invoiceId }) => {
  const { data, isLoading, isError, error } = useQuery([keys.invoiceHistory, invoiceId], () => getInvoiceHistory(invoiceId), {
    refetchOnMount: 'always',
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (isError) return <Alert severity='error'>{(error as Error)?.message || 'Failed to load invoice history'}</Alert>;
  if (!data?.length) return <Typography color='text.secondary'>No history recorded for this invoice.</Typography>;

  return (
    <Box>
      {data.map((entry, index) => {
        const name = getUserName(entry);
        const isLast = index === data.length - 1;
        return (
          <Box key={entry.id} sx={{ display: 'flex', gap: 1.5 }}>
            {/* Rail */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: ACTION_COLORS[entry.action] }}>{getInitials(name)}</Avatar>
              {!isLast && <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', my: 0.5 }} />}
            </Box>

            {/* Entry */}
            <Box sx={{ flex: 1, pb: isLast ? 0 : 2.5, minWidth: 0 }}>
              <Typography variant='body2'>
                <b>{name}</b> {ACTION_LABELS[entry.action] ?? entry.action}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {entry.changedOn && !entry.changedOn.startsWith('0001') ? formatDateTimeDescriptive(entry.changedOn) : 'Date unknown'}
              </Typography>

              {entry.reason && (
                <Typography
                  variant='body2'
                  sx={{ mt: 0.75, pl: 1.25, borderLeft: '3px solid', borderColor: 'divider', fontStyle: 'italic', color: 'text.secondary' }}
                >
                  {entry.reason}
                </Typography>
              )}

              {entry.changes.length > 0 && (
                <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: 'grey.100' }}>
                  {entry.changes.map(change => (
                    <Typography key={change.field} variant='body2' sx={{ wordBreak: 'break-word' }}>
                      <Box component='span' sx={{ color: 'text.secondary' }}>
                        {INVOICE_HISTORY_FIELD_LABELS[change.field] ?? change.field}:
                      </Box>{' '}
                      {entry.action === 'Updated' && (
                        <>
                          <Box component='span' sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            {formatInvoiceHistoryValue(change.field, change.oldValue)}
                          </Box>
                          {' → '}
                        </>
                      )}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {formatInvoiceHistoryValue(change.field, entry.action === 'Deleted' ? change.oldValue : change.newValue)}
                      </Box>
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const InvoiceHistoryDialog: React.FC<InvoiceHistoryDialogProps> = ({ open, invoiceId, invoiceNumber, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
    <DialogTitle>Invoice History{invoiceNumber ? ` — ${invoiceNumber}` : ''}</DialogTitle>
    <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
      <CloseIcon />
    </IconButton>
    <DialogContent dividers>{invoiceId && <HistoryTimeline invoiceId={invoiceId} />}</DialogContent>
  </Dialog>
);

export default InvoiceHistoryDialog;
